import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { CalendarCheck, CalendarPlus, Heart, ListPlus, MoreHorizontal, Navigation } from 'lucide-react'
import { Banner, Button, Empty, Field, IconButton, Sheet, useToast } from '../ui'
import { Boundary } from './Boundary'
import { EntityChips } from './EntityChips'
import { EventActions } from './EventActions'
import { VenueLink } from './VenueLink'
import { TrainPlanLine } from './TrainsCard'
import { AddToListSheet } from './AddToListSheet'
import { PlacedChips, nextShowing, occurrencesOf, showingLabel } from './ShowingChips'
import { Hero, FilmPoster } from './CardBody'
import { useCommitments, useEntryResearch, useEntryState, useFeedDetail, useSeries, useShowings, useVenues } from '../lib/queries'
import {
  useDismissEntry, useForgetEntry, useRemoveCommitment, useSaveEntry, useScheduleEntry,
} from '../lib/mutations'
import {
  DATE_MARK, anyTicketFact, dateTicket, ticketLine, useShowingTickets, useTicketCheck, type ShowingTicket,
} from '../lib/tickets'
import { addDays, fmt, iso } from '../lib/calendar'
import { hereZone, midnightIsoIn, zoneOfCity } from '../lib/zone'
import { currentRegion, useRegionsNow, type RegionTown, type Region } from '../lib/regions'
import { calendarTapAction, calendarTapLabel } from '../lib/calendarTap'
import {
  PEOPLE_FACT_LABEL, calendarWindow, cityOf, filmScores, formatDuration, generatedSummary,
  heroIsVenue, idsOf, isHere, knownLanguage, sheetFacts, sheetText, signalFeatures, type Fact, type Point,
} from '../lib/entries'
import { placePoint, useRegionPlace } from '../lib/currentRegion'
import { browseQuery, floorGroupsFor, groupsFor, type Chip, type Group } from '../lib/entities'
import { clubWhat, hostSeries, lineupLine, seriesName } from '../lib/night'
import { go } from '../lib/router'
import { sentences } from '../lib/exhibitionText'
import { FORM_LABEL, classicalForm } from '../lib/classicalForm'
import { hueOf, kindLine, metaLine, rowTitle, setPlace, whatLine } from '../lib/rowMeta'
import { languageLine } from '../lib/filmRank'
import { uniqueShowings } from '../lib/places'
import { placeAddress, sheetPlace, type SheetPlace } from '../lib/sheetPlace'
import { useWriteError } from '../lib/useAction'
import type { FeedRow } from '../lib/types'
import type { ShowingLite } from './CardBody'
import './EventSheet.css'

const noop = () => {}
const NONE: ShowingLite[] = []
const NO_TICKETS: ShowingTicket[] = []

/**
 * One event, in full. Every tap on a row is this.
 *
 * Rebuilt on `ui/Sheet`, which brings the snap points, the scrim from the
 * first snap, the focus trap and the history entry that makes the phone's back
 * gesture close the sheet instead of leaving the tab (UI-audit F08). Nothing
 * about a sheet is hand-rolled here.
 *
 * **It owns its own writes (E4).** Home, Feed, Saved and Venues used to pass
 * `saved`, `onSave` and `onDismiss` — four call sites, three of which had no
 * handler to give and two of which wrote a taste signal the third did not.
 * The sheet reads `entry_state` and calls the mutations itself, exactly as
 * `EventRow` does, so there is one save path in the app.
 *
 * **There is no "why this is here" line.** H2, Doug verbatim: "I don't want to
 * have an AI rationale. Just give me what the thing is." `score_reasons` is
 * not read here and is not fetched for it; the sheet's only paragraph is the
 * house's own text, verbatim, with a link to where it came from where it was
 * not the house that wrote it.
 */
/**
 * The sheet, behind its own error boundary (UI-audit a4-04). Every screen
 * hosts one of these, and the body reads more of a row than anything else in
 * the app — so a row it cannot draw shows a sheet that says so and still
 * closes, rather than taking the screen under it down too. Opening another
 * event clears it (`resetKey`).
 */
export function EventSheet({ e, onClose }: { e: FeedRow | null; onClose: () => void }) {
  return (
    <Boundary
      where="event-sheet" resetKey={e?.id ?? null}
      fallback={retry => (
        <Sheet open={!!e} onOpenChange={o => { if (!o) onClose() }} title="Something went wrong">
          <Empty title="This event could not be shown.">
            <p>The rest of the app still works.</p>
            <Button variant="secondary" size="sm" onClick={retry}>Retry</Button>
          </Empty>
        </Sheet>
      )}
    >
      <EventSheetBody e={e} onClose={onClose} />
    </Boundary>
  )
}

function EventSheetBody({ e, onClose }: { e: FeedRow | null; onClose: () => void }) {
  // The row is held after `e` goes null so the sheet has something to draw
  // while Vaul animates it down — otherwise closing shows an empty sheet
  // sliding off the bottom of the screen.
  const [last, setLast] = useState<FeedRow | null>(e)
  useEffect(() => { if (e) setLast(e) }, [e])
  const row = e ?? last

  const today = iso(new Date())
  const now = new Date()
  const [view, setView] = useState<'sheet' | 'menu' | 'list'>('sheet')
  useEffect(() => { if (!e) setView('sheet') }, [e])

  // WP 3.3: which chip is picked, and whether the inline date field for an
  // undated run is open. Both reset when the sheet moves on to a different
  // event — reopening the same one keeps whatever was picked, the same way
  // `view` above only resets on close, not on every render.
  const [manualSelected, setManualSelected] = useState<ShowingLite | null>(null)
  const [pickingDate, setPickingDate] = useState(false)
  const [pickDate, setPickDate] = useState(today)
  // calendar-pick-date: Calendar was tapped on a row with several nights and
  // the sheet is asking which. Its own flag, not "manualSelected is null":
  // that one is "the person chose a chip", this is "the sheet asked", and
  // they differ exactly while a choice is being made (chip tapped, Add not
  // yet) — CLAUDE.md's one-value-two-jobs bug, named before it is written.
  const [picking, setPicking] = useState(false)
  useEffect(() => { setManualSelected(null); setPickingDate(false); setPicking(false) }, [row?.id])
  // Where the chips are, so the ask can scroll them into view: the sheet
  // opens on the head, and a question drawn below the fold is not asked.
  const showingsRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (picking) showingsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [picking])

  const { data: detail } = useFeedDetail(row?.id ?? null)
  const { data: showQ } = useShowings()
  const { data: state } = useEntryState()
  const { data: commitments } = useCommitments()
  // WP 8.6: called unconditionally, above the `if (!row) return` guard below
  // — CLAUDE.md's own trap, "a hook below an early return never runs on the
  // first render". `useTicketCheck` takes `entryId: string | null` exactly
  // so it can sit here with every other hook that reads `row?.id`.
  const ticketCheck = useTicketCheck(row?.id ?? null)

  const saveM = useSaveEntry()
  const forgetM = useForgetEntry()
  const dismissM = useDismissEntry()
  const schedule = useScheduleEntry()
  const removeCommitmentM = useRemoveCommitment()
  const toast = useToast()
  const writeErr = useWriteError(schedule)

  const saved = !!row && (state ?? []).some(r => r.entry_id === row.id && r.state === 'saved')
  const showings = row ? gather(showQ?.byEntry, showQ?.rows, row) : NONE

  // A commitment already carries this entry — E1's "In the Year" state. A
  // merged film card's ids all point at the one card, so any of them having a
  // commitment counts.
  const ids = row ? idsOf(row) : []
  const commitment = row ? (commitments ?? []).find(c => c.entry_id && ids.includes(c.entry_id)) ?? null : null
  // WP 9.4: the butler's own findings about this event, across a merged
  // film card's whole group. Called unconditionally, above the `if (!row)
  // return` guard below — the same reason `ticketCheck` is (CLAUDE.md's own
  // trap, "a hook below an early return never runs on the first render").
  const research = useEntryResearch(ids).data ?? []
  // WP 10.2: each date's ticket status as the harvest (10.3) or a tap (10.4)
  // wrote it. Above the guard for the same reason.
  const heldTickets = useShowingTickets(ids).data ?? NO_TICKETS

  const win = row ? calendarWindow(row, today) : null
  const passed = !!row?.run_start && !win
  const schedWin = win ?? (row && !row.run_start ? { min: today, max: addDays(today, 730) } : null)

  const occurrences = uniqueShowings(occurrencesOf(showings)).filter(s => new Date(s.starts_at).getTime() >= now.getTime())
  // `nextShowing` answers over every place at once, as before: the chip that
  // starts selected is the set's next night, wherever it is.
  const selected = manualSelected ?? nextShowing(showings, now)
  // What a Calendar tap does — `lib/calendarTap.ts`, tested offline. `chosen`
  // is the person's tap, never `selected` (which has a default nobody chose).
  const tapAction = calendarTapAction({
    occurrences: occurrences.length, chosen: manualSelected != null, hasWindow: !!schedWin,
  })

  // WP 2.4: `summary` moved out of `feed_lite` and into `feed_detail` — the
  // blurb is the sheet's now that the card is gone, so the row no longer
  // carries it for every one of 1,788 rows. It fills in when the request
  // lands, with no loading box; the sheet has everything else immediately.
  const summary = detail?.summary ?? null
  const sourceUrl = detail?.summary_source_url ?? null

  // E5: who is on this event, from `feed_detail` — the only view that carries
  // the names and E2's facts (the row's own `people` is `[{id, role}]` and
  // belongs to the Browse filter, never to a label). Empty until the request
  // lands, which is what leaves the `<dd>`s showing `sheetFacts`' own strings
  // in the meantime. Above the `if (!row) return` guard with every other
  // hook — CLAUDE.md's "a hook below an early return never runs on the first
  // render", which on a cold load is exactly the render this block gets.
  //
  // Nightlife N7: a club night whose page split its lineup over more than
  // one floor is grouped by floor instead (`floorGroupsFor`); everything
  // else, a one-room club night included, is grouped by role — DJs, Live.
  // `row.night` first, `feed_detail`'s copy second: a row cached before
  // `feed_page` sent `night` has no key, and the detail always has it.
  const night = row?.night ?? detail?.night ?? null
  const groups = useMemo(() => floorGroupsFor(detail, night) ?? groupsFor(detail), [detail, night])
  // The party's name, for the head's eyebrow link. Above the guard with the
  // other hooks. `feed_detail.series` carries names too, so the sheet can
  // name the party before the `series` table has arrived.
  const { data: seriesById } = useSeries()
  // The trip line's drive time is measured from the current region's pin (D5):
  // the home pin in Berlin, the Concord pin in Boston, nothing where the
  // person has no place. Above the `!row` return below, as every hook here is.
  const home = placePoint(useRegionPlace())
  // C2.1: the region being looked at, for the trip line and the "away" tag.
  const regions = useRegionsNow()
  const region = currentRegion(regions)
  // UI-audit a1-01: the address, coordinates and booking link of a night at
  // another cinema than the row's own. Above the guard with the other hooks.
  const { data: venues } = useVenues()

  if (!row) return <Sheet open={false} onOpenChange={noop} />

  const save = () => saveM.mutate({ ids: idsOf(row), features: signalFeatures(row) })
  const forget = () => forgetM.mutate({ ids: idsOf(row) })
  const dismiss = () => {
    dismissM.mutate({ ids: idsOf(row), features: signalFeatures(row) })
    toast({ text: 'Hidden', action: { label: 'Undo', onClick: forget } })
    onClose()
  }

  /**
   * K6: a chip tap is a Browse filter, not a person page — `?person=` /
   * `?ensemble=`, which is a link that still means the same thing after a
   * reload, exactly as `?kind=` and `?when=` already are.
   *
   * The sheet closes and Browse opens at the top. `browseQuery` writes the one
   * key and nothing else: the sheet does not know which when/kind/search
   * Browse was last on (it opens from Today and a place page too), and
   * carrying a filter the tap did not ask for is how "no concerts by this
   * conductor" gets shown for a conductor who has four next month.
   *
   * `go` before the close would be the same thing — both are batched in this
   * handler — but the hash is written synchronously either way, which is what
   * `ui/Sheet`'s cleanup checks before deciding whether to pop its own history
   * entry. Its guard is the reason this does not undo the navigation.
   */
  const pickEntity = (chip: Chip) => {
    onClose()
    go('browse', null, browseQuery(chip))
    window.scrollTo({ top: 0 })
  }

  /**
   * E1: Calendar writes straight through when a showing is already picked —
   * no picker, no second sheet — and carries the exact `starts_at` a test in
   * `test/ics.test.ts` covers the round trip for. `commit`'s own `catch` does
   * nothing further: the rejection is already on `schedule`, which is where
   * `writeErr` above reads it from.
   */
  const commit = async (startsAt: string) => {
    try {
      const res = await schedule.mutateAsync({
        entryId: row.id, startsAt, title: row.title, features: signalFeatures(row),
      })
      const id = (res as { data: { id: string } | null }).data?.id ?? null
      toast({
        text: 'Added to the Year',
        action: { label: 'Undo', onClick: () => { if (id) removeCommitmentM.mutate(id) } },
      })
      setPickingDate(false)
      setPicking(false)
    } catch { /* left on screen: writeErr reads schedule.error */ }
  }

  /**
   * calendar-pick-date: one tap writes when there is one night, or when a
   * chip was tapped first; several nights and no tap asks instead — the
   * chips become the picker, with a line and an Add above them. The ask is
   * the chips already on the sheet rather than a second list of dates: a
   * film at ten cinemas is 37 nights, and the chips are the one drawing of
   * them that already fits the screen and already says which cinema.
   */
  const tapCalendar = () => {
    switch (tapAction) {
      case 'write': if (selected) void commit(selected.starts_at); return
      case 'pick': setPicking(true); return
      case 'date-input': if (schedWin) { setPickDate(schedWin.min); setPickingDate(true) } return
      case 'passed': return
    }
  }

  /**
   * The action menu sits *on top of* this sheet rather than replacing it —
   * WP 3.3 moved the calendar picker inline (rule 2, no second sheet from
   * this one), so the menu is the only thing left that stacks.
   *
   * It used to be an early return, which unmounted the `Drawer.Root` under it
   * mid-flight — and Vaul left the outgoing scrim in the DOM, `aria-hidden` and
   * intercepting every pointer event, so the menu drew correctly and could not
   * be tapped. Rendering found it; nothing about the markup looks wrong.
   *
   * `ui/Sheet` is built to stack — that is what its module-level `stack` and the
   * one shared `popstate` listener are for, so that closing the top sheet pops
   * the entry it pushed and the pop reaches the one below exactly as a real
   * back-press would. Stacking is the supported shape; swapping was not.
   */
  const isFilm = row.type === 'screening' || row.discipline === 'film' || row.type === 'release'
  const title = rowTitle(row)
  // The same line the row showed, from the same function, so the sheet cannot
  // disagree with the row you tapped about when the thing is.
  const line = metaLine(row, { now: new Date(), showings })
  // WP 6.1: the head opens on the row's own three strings — the kind line,
  // the title and what it is — so tapping a row never opens something that
  // reads like a different event. `when` is not taken from here: the head has
  // its own line for it below, and saying the date twice is a sheet with two
  // answers.
  const form = classicalForm(row)
  const head = kindLine(row, { now: new Date(), showings, form: form ? FORM_LABEL[form] : null })
  // Nightlife N7: the same what line the row printed for a club night —
  // format and doors (`clubWhat`) — so the sheet cannot disagree with it.
  const isClub = row.type === 'club_night'
  const what = (isClub ? clubWhat(night) : null) ?? whatLine(row, { showings })
  // The party whose night it is, as the eyebrow's link to its page. The row
  // first (`[{id, role}]`), the detail's full objects second; the name from
  // the `series` table first, the detail's copy second.
  const hostId = isClub ? hostSeries(row.series ?? detail?.series) : null
  const hostName = hostId
    ? seriesName(seriesById, hostId) ?? detail?.series?.find(s => s.id === hostId)?.name ?? null
    : null
  const openSeries = () => {
    if (!hostId) return
    onClose()
    go('browse', null, { series: hostId })
    window.scrollTo({ top: 0 })
  }
  // WP 8.6: only the kinds with a booking page worth asking about — the same
  // gate `rowMeta.ts`'s `hueOf`/`typeLabel` use for "this is a concert or
  // opera", restated here rather than imported (this file does not touch
  // `rowMeta.ts`, per the brief). `ticketCheck` itself is declared above the
  // `if (!row) return` guard — see the comment there.
  //
  // WP 10.7 (10.6 finding 4): checked against *some* link before deciding the
  // kind is checkable, without yet deciding *which* link `ticket` uses below
  // — `ticket` used to fall back to `row.url` unconditionally, so a film or a
  // free permanent exhibition (BAUBAU, "Prime Time") drew a labelled Tickets
  // button pointed at its own source page, which is not a ticket page.
  const ticketCheckable = !!(row.booking_url ?? row.url) && (row.type === 'concert' || row.discipline === 'opera')
  // UI-audit a1-01: where the highlighted night is — Where, Directions,
  // Tickets and the `.ics` all follow it (Doug's NEXT.md question answered
  // yes). The highlighted chip, not only a tapped one: it is the night
  // Calendar writes and the chip the sheet draws as picked, so a Where for
  // any other night would be the sheet disagreeing with itself. The row's
  // own place is the answer only where there is no showing, or the showing
  // is at the row's venue — `lib/sheetPlace.ts`, tested offline.
  const place = sheetPlace(row, selected, id => venues?.byId.get(id))
  // a1-28: the head's venue opens its place page — only where the head names
  // one place. A set ("10 cinemas") is none, and the Where row below names
  // the night's own.
  const headVenueId = setPlace(row) ? null : place.id
  // `row.url` is only a ticket destination when the kind above says it's
  // worth asking about; otherwise a real `booking_url` or nothing. A night
  // at another house gets that house's own link or none — never the row's,
  // which is the row's cinema's (VATERLAND's Il Kino kinoheld link on all
  // eleven).
  const rowTicket = row.booking_url ?? (ticketCheckable ? row.url : null)
  const ticket = place.own ? rowTicket : place.house ? place.booking ?? rowTicket : place.booking
  // WP T.2 / 10.2: the shop the check found on the page (the Philharmoniker's
  // presale link, say) is a better destination than `booking_url`, so the
  // Tickets button opens it once a tap has found one — only an absolute link,
  // the same test `hostOf` makes. No separate "Buy at" line.
  const shop = place.house && ticketCheck.data?.url && hostOf(ticketCheck.data.url) ? ticketCheck.data.url : null
  const ticketHref = shop ?? ticket
  const ticketHost = ticketHref ? hostOf(ticketHref) : null
  // tix-1: Tickets for a night at another cinema with no page of its own is
  // the listing site's film page (`sheetPlace`'s `via`) — said on the button,
  // so it does not read as that cinema's own box office.
  const ticketVia = !shop && !place.own && ticketHref === place.booking ? place.via : null
  // WP 10.2: one status per upcoming date, the chips' own dates, and the
  // quiet line under them. Drawn for the kinds a tap can check and for
  // anything the harvest already has a fact about; nothing for a film or a
  // gallery that has neither.
  const dated = occurrences.map(s => dateTicket(s, heldTickets, ticketCheck.data))
  const markOf = (s: ShowingLite) => {
    const t = dateTicket(s, heldTickets, ticketCheck.data)
    return t.status ? DATE_MARK[t.status] ?? null : null
  }
  const showTicketLine = !passed && (ticketCheckable || anyTicketFact(dated, row))
  const tline = ticketLine({
    dates: dated, entry: row, check: ticketCheck.data,
    checking: ticketCheck.isFetching, failed: ticketCheck.isError, now, tz: hereZone(),
  })
  const checkTickets = () => void ticketCheck.refetch()
  // Up to three sentences of the house's own published text where the walk
  // found one, and TMDb's own plot where it did not — never both, and never a
  // model's words next to either (`generatedSummary` is what tells them apart).
  const longText = sheetText(row)
  const filmPlot = !longText && !summary && row.film?.overview ? sentences(row.film.overview, 3, 400) : null
  const found = generatedSummary(summary, sourceUrl)
  // a1-31: only known facts reach this list, so a row that knows none has
  // none — and no empty `<dl>` holding a margin open where they would be.
  const factRows = withChips(withLineup(facts(row, isFilm, occurrences, place, home, region, regions.towns), row, night, groups), groups)

  return (
    <>
    <Sheet
      open={!!e}
      onOpenChange={o => { if (!o) onClose() }}
      snap
      className="event-sheet"
      actions={
        /* WP 10.2: one row and a menu. The bar had grown by accretion — Save,
           Calendar, Share, More, a full-width "Tickets at <host>", Check
           tickets, Check again, a status banner — to three rows and 224px.
           What left it: Share and Check tickets are in the menu, the status
           is a line under the dates, and the host is in `title`. At 390px
           four labelled buttons do not fit (the report has the widths), so
           Calendar is the one that goes to an icon; it sits with More so the
           two labelled buttons, and the two icons, read as pairs. */
        <div className="event-sheet__row">
          <Button
            variant={saved ? 'secondary' : 'primary'}
            icon={<Heart size={20} fill={saved ? 'currentColor' : 'none'} />}
            onClick={() => (saved ? forget() : save())}
          >
            {saved ? 'Saved' : 'Save'}
          </Button>
          {/* A real anchor, not a Button with an onClick: a ticket link is
              something you long-press to copy or open elsewhere. `Button`'s
              paint is on its `__face` span rather than on the element itself
              (so `sm` can be drawn 34px and hit 44px), so an anchor wearing
              its classes has to carry the same two spans or it gets no paint
              at all. Labelled "Tickets" whatever the host: a host name is a
              fact to check, not a label to read (Doug's note 6). The one
              exception is tix-1's "via cinematic": there the site is not
              the host of a box office but the reason the link is not this
              cinema's own, which is a thing to read before tapping. */}
          {ticketHref && (
            <a className={ticketVia ? 'u-btn u-btn--secondary u-btn--md event-sheet__ticket is-via' : 'u-btn u-btn--secondary u-btn--md event-sheet__ticket'}
               href={ticketHref} target="_blank" rel="noopener noreferrer"
               title={ticketHost ?? undefined}
               aria-label={ticketVia ? `Tickets via ${ticketHost ?? ticketVia}` : ticketHost ? `Tickets at ${ticketHost}` : undefined}>
              <span className="u-btn__face">
                <span className="u-btn__label">Tickets</span>
                {ticketVia && <span className="event-sheet__via">via {ticketVia}</span>}
              </span>
            </a>
          )}
          {commitment ? (
            <IconButton label="In the Year" title="In the Year" className="event-sheet__icon" disabled>
              <CalendarCheck size={20} />
            </IconButton>
          ) : (
            <IconButton
              label={passed ? 'Already passed' : calendarTapLabel(tapAction)}
              title={passed ? 'Already passed' : calendarTapLabel(tapAction)}
              className="event-sheet__icon"
              disabled={passed || !schedWin}
              onClick={tapCalendar}
            >
              <CalendarPlus size={20} />
            </IconButton>
          )}
          {/* a2-23: Add to list one tap from the sheet, not More → Add to
              list → the sheet. An icon, like Calendar, for the width. */}
          <IconButton label="Add to list" title="Add to list" className="event-sheet__icon" onClick={() => setView('list')}>
            <ListPlus size={20} />
          </IconButton>
          <IconButton label="More" title="More" className="event-sheet__icon" onClick={() => setView('menu')}>
            <MoreHorizontal size={20} />
          </IconButton>
        </div>
      }
    >
      {/* The picture of the thing itself, at the sheet's own ratio. Never the
          venue's photograph: a photograph of the Philharmonie over a Bruckner
          programme is scene-setting, and the sheet has a `Where` row that
          already says the building (D7). */}
      {!isFilm && !heroIsVenue(row) && (
        <div style={{ viewTransitionName: `hero-${row.id}` }}>
          <Hero e={row} ratio="3 / 2" onOpen={noop} />
        </div>
      )}

      {/* A poster is 2:3 and the sheet is a phone's width, so a poster across
          the top would be taller than the screen and leave the title below the
          fold. It sits beside the head instead — which is also the one place
          the film facts read as belonging to the picture. */}
      <div className={isFilm ? 'event-sheet__head event-sheet__head--film' : 'event-sheet__head'}>
        {isFilm && (
          <div className="event-sheet__poster" style={{ viewTransitionName: `hero-${row.id}` }}>
            <FilmPoster e={row} onOpen={noop} />
          </div>
        )}
        <div className="event-sheet__headtext">
      <p className="event-sheet__kind" style={{ color: hueOf(row) }}>
        {[head.kind, head.genre].filter(Boolean).join(' · ')}
        {/* Nightlife N7: the party, where the night has a host — a link to
            its page (`?series=`), the one way in. The row cannot carry it:
            `ui/Row`'s whole body is one <button>, and a button inside a
            button is not a link anyone can tap. */}
        {hostName && (
          <>
            <span aria-hidden="true">·</span>
            <button type="button" className="event-sheet__series" onClick={openSeries}>{hostName}</button>
          </>
        )}
        {/* Said plainly when nobody knows, rather than letting an absent city
            read as Berlin. */}
        {!isHere(row, region, regions.towns) && <span className="event-sheet__away">{cityOf(row) ?? 'City not given'}</span>}
      </p>

      {/* The same string the row showed (E2): tapping a row never opens
          something that looks like a different event. */}
      <h2 className="event-sheet__title">{title}</h2>

      {/* When and where, in the head rather than only in the fact list below.
          The sheet used to open at three fifths of the screen, where a
          picture, a kind and a title filled all of it — so the two things you
          actually decide on were a drag away (it opens full since WP 10.2, and
          this line is still what the first screen is for). The `When` row is dropped from the facts in
          exchange (`facts()` below): `Where` keeps its place there because the
          full address and the Directions link belong with it, and this line
          names the building only. */}
      <p className="event-sheet__when">
        <b>{line.when}</b>
        {line.venue && <>{' · '}{headVenueId
          ? <VenueLink as="button" id={headVenueId} onOpen={onClose}>{line.venue}</VenueLink>
          : line.venue}</>}
        {line.fact && <>{' · '}{line.fact}</>}
      </p>

      {what && <p className="event-sheet__who">{what}</p>}
        </div>
      </div>

      {/* WP 3.3: chips for a run with clocked dates, one line for a run that
          only has a span, "Already happened" once the last one has passed —
          never more than one of the three at a time. */}
      <div data-slot="showings" className="event-sheet__showings" ref={showingsRef}>
        {writeErr && <Banner tone="bad">{writeErr}</Banner>}
        {/* calendar-pick-date: the ask. Sticky at the top of the sheet's
            scroll, so on a film at ten cinemas the Add is still on screen
            when the chip that was tapped is the thirtieth. Names the chip
            once one is tapped — the default stays highlighted, and Add
            writes whichever is highlighted, so a second tap on Add is the
            next night confirmed rather than a dead end. */}
        {picking && !commitment && selected && (
          <div className="event-sheet__pickline" role="status">
            <p className="event-sheet__pickline-text">
              {manualSelected
                ? <>
                    <b>{showingLabel(manualSelected.starts_at, zoneOfCity(row.city))}</b>
                    {/* The place only where the dates span more than one: on
                        a film at nine cinemas it is the fact that tells two
                        21:00s apart; on a concert in one hall it is the
                        head's line said again. */}
                    {manualSelected.venue && occurrences.some(s => s.venue !== manualSelected.venue)
                      ? ` · ${manualSelected.venue}` : ''}
                  </>
                : 'Pick a date, then Add'}
            </p>
            <Button variant="primary" size="sm" busy={schedule.isPending} onClick={() => void commit(selected.starts_at)}>
              Add
            </Button>
          </div>
        )}
        {passed ? (
          <p className="event-sheet__count">Already happened</p>
        ) : occurrences.length > 0 ? (
          <>
            {/* One count per night: `occurrences` is the future nights once
                each, where `showings` counted a night twice when two sources
                listed it (cards WP4). */}
            <p className="event-sheet__count">
              <b>{occurrences.length}</b>{occurrences.length === 1 ? ' date' : ' dates'}
              {range(occurrences) && <> · {range(occurrences)}</>}
            </p>
            {/* Cards WP4: a set playing several cinemas is grouped by
                cinema, in `places` order — the one playing soonest first —
                with the versions it plays beside its name. Each group shares
                one `selected`, so a tap anywhere picks that night and
                Calendar writes it (`PlacedChips`, shared with the menu). */}
            <PlacedChips
              key={row.id}
              row={row} occurrences={occurrences} showings={showings}
              value={selected} onChange={setManualSelected} now={now} mark={markOf}
              fold asking={picking}
            />
          </>
        ) : row.run_start ? (
          <p className="event-sheet__count">
            Any day until {fmt(row.run_end ?? row.run_start, { day: 'numeric', month: 'short' })}
          </p>
        ) : null}

        {/* WP 10.2: tickets as one quiet line, with Check at its end — where
            the Check tickets / Check again buttons and the "Couldn't tell —
            open the page" banner were. Never automatic: `useTicketCheck` is
            `enabled: false` and runs only on this tap or the menu's (WP 8.6,
            Doug: "keeping it as cheap as possible"). */}
        {showTicketLine && (
          <div className="event-sheet__tickets" aria-live="polite">
            <p className="event-sheet__tickets-text">
              {tline.text}
              {tline.unreadable && ticketHref && (
                <>{' · '}<a href={ticketHref} target="_blank" rel="noopener noreferrer">Tickets ↗</a></>
              )}
            </p>
            {ticketCheckable && (
              <Button variant="quiet" size="sm" busy={ticketCheck.isFetching} onClick={checkTickets}>
                Check
              </Button>
            )}
          </div>
        )}

        {/* The old sheet's job, inline: a run with nothing clocked has no chip
            to write from, so Calendar opens this instead of a second sheet
            (rule 2). `midnightIsoIn` carries the real offset of the current
            region's zone for the picked day, not a fixed one — see its own
            note on why that matters here specifically. */}
        {pickingDate && schedWin && (
          <div className="event-sheet__datepick">
            <Field label="Date">
              <Field.Input
                type="date" value={pickDate} min={schedWin.min} max={schedWin.max}
                disabled={schedule.isPending}
                onChange={ev => setPickDate(ev.target.value)}
              />
            </Field>
            <Button
              variant="secondary" disabled={schedule.isPending}
              onClick={() => void commit(midnightIsoIn(pickDate, hereZone()))}
            >
              Add
            </Button>
          </div>
        )}
      </div>

      {factRows.length > 0 && <dl className="event-sheet__facts">
        {/* Keyed on the position as well as the label: E5 can add a row
            (`withChips`), and two rows sharing a key is React dropping one of
            them without a word. */}
        {factRows.map((f, i) => (
          <Fragment key={`${f.label}:${i}`}>
            <dt>{f.label}</dt>
            <dd>
              {f.chips
                ? <EntityChips chips={f.chips} onPick={pickEntity} />
                : f.href
                ? <a href={f.href} target="_blank" rel="noopener noreferrer">{f.value}</a>
                : f.label === 'Where' && place.id && place.venue && f.value.startsWith(place.venue)
                ? <><VenueLink as="button" id={place.id} onOpen={onClose}>{place.venue}</VenueLink>{f.value.slice(place.venue.length)}</>
                : f.value}
              {/* E4's Directions: Maps, from the coordinates the venue row
                  already has. Only where they exist — a query built from a
                  street name lands on the wrong Goethestraße. */}
              {f.label === 'Where' && place.lat != null && place.lng != null && (
                <a className="event-sheet__dir"
                   href={`https://maps.google.com/?q=${place.lat},${place.lng}`}
                   target="_blank" rel="noopener noreferrer">
                  <Navigation size={14} /> Directions
                </a>
              )}
            </dd>
          </Fragment>
        ))}
        {/* C4.2: the train that gets him there and the last one home, for a
            Boston or Cambridge night when the place has a stop. Its own
            component: the hooks it needs read the night chosen above, which
            is below this sheet's early return. Draws its own dt and dd, or
            nothing. */}
        <TrainPlanLine start={selected?.starts_at ?? null} venue={place} />
      </dl>}

      {/* The house's own words, verbatim, or a model's with a link to the page
          it read — never the two looking alike, and never a sentence about why
          this is in the feed (B1, H2). */}
      {(longText ?? summary) && (
        <p className="event-sheet__text">
          {longText ?? summary}
          {!longText && found && (
            <a className="event-sheet__found" href={found} target="_blank" rel="noopener noreferrer">
              Written from {hostOf(found) ?? 'its source'}
            </a>
          )}
        </p>
      )}
      {filmPlot && <p className="event-sheet__text">{filmPlot}</p>}

      {programme(row).length > 0 && (
        <div className="event-sheet__prog">
          <b>Programme</b>
          {programme(row).map((p, i) => <p key={i} className={p.main ? 'is-main' : undefined}>{p.text}</p>)}
        </div>
      )}

      {reviews(row).length > 0 && (
        <div className="event-sheet__prog">
          <b>Reviews</b>
          {reviews(row).map(r => (
            <p key={r.url}>
              <a href={r.url} target="_blank" rel="noopener noreferrer">
                {r.source} · {r.title} · {fmt(r.published_on, { day: 'numeric', month: 'short' })}
              </a>
            </p>
          ))}
        </div>
      )}

      {/* WP 9.4: what the butler already found out, saved against this
          event by `save_research` — shown as research, never as the sheet's
          own summary (README, *Traps*: "the one place a model writes
          prose, and it is shown as research"). Nothing when there is none:
          no heading, no placeholder. */}
      {research.length > 0 && (
        <div className="event-sheet__prog">
          <b>Research</b>
          {research.map(r => (
            <div key={r.id} className="event-sheet__research">
              <p className="event-sheet__research-kind">{RESEARCH_LABEL[r.kind] ?? r.kind}</p>
              <p>{r.body}</p>
              {r.sources.length > 0 && (
                <p className="event-sheet__research-sources">
                  {r.sources.map(s => (
                    <a key={s.url} href={s.url} target="_blank" rel="noopener noreferrer" title={s.title || undefined}>
                      {hostOf(s.url) ?? s.title}
                    </a>
                  ))}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </Sheet>

    {/* a2-23: always mounted, its own `open` toggled — the shape
        EventActions gives it, so the close animation plays. */}
    <AddToListSheet entry={row} open={view === 'list'} onClose={() => setView('sheet')} />

    {view === 'menu' && (
      <EventActions
        e={row}
        saved={saved}
        showings={showings}
        chosen={manualSelected}
        commitment={commitment}
        summary={summary}
        onClose={() => setView('sheet')}
        onSave={save}
        onForget={forget}
        onDismiss={dismiss}
        onCheckTickets={ticketCheckable ? checkTickets : undefined}
        stacked
      />
    )}
    </>
  )
}

// ---------------------------------------------------------------------------
// What the sheet says
// ---------------------------------------------------------------------------

/** The host, for a link that has to be checkable rather than pretty.
 *
 *  Null where there is no host to name: a few `booking_url`s are site-relative
 *  paths the harvest never resolved, and the first render of this printed
 *  "Tickets at /film/en/vaterland_2026" — a label promising a place, naming a
 *  fragment. The button says "Tickets" and still goes there. */
const hostOf = (url: string): string | null => {
  try { return new URL(url).host.replace(/^www\./, '') || null } catch { return null }
}

/** `entry_research.kind`'s own four values, in words — `RESEARCH_KINDS` in
 *  `src/butler/tools/manifest.ts`, restated here for the same reason
 *  `EXCLUSIONS` in `prompt.ts` restates `taste/selection.md`: this file does
 *  not import the Worker's source. */
const RESEARCH_LABEL: Record<string, string> = {
  reviews: 'Reviews', background: 'Background', availability: 'Availability', programme: 'Programme',
}

/** Every showing this row stands for. A merged film card owns several ids, so
 *  the index is asked once per id rather than the 2,700 rows being scanned. */
function gather(
  byEntry: Map<string, ShowingLite[]> | undefined,
  rows: ShowingLite[] | undefined,
  e: FeedRow,
): ShowingLite[] {
  if (!byEntry || !rows) return NONE
  const ids = idsOf(e)
  if (ids.length === 1) return byEntry.get(ids[0]) ?? NONE
  const set = new Set(ids)
  return rows.filter(s => set.has(s.entry_id))
}

/** First and last of the dates, where there is more than one. Two formatted
 *  dates and a dash: not a sentence about a run. */
function range(showings: ShowingLite[]): string | null {
  const dates = showings.filter(s => s.kind !== 'run').map(s => s.starts_at).sort()
  if (dates.length < 2) return null
  const a = fmt(iso(new Date(dates[0])), { day: 'numeric', month: 'short' })
  const b = fmt(iso(new Date(dates[dates.length - 1])), { day: 'numeric', month: 'short' })
  return a === b ? a : `${a} – ${b}`
}

/** The fact list, plus the two rows only the sheet has room for: where the
 *  listing came from, and — for a film — the facts `sheetFacts` leaves to the
 *  card. */
function facts(
  e: FeedRow, isFilm: boolean, occurrences: ShowingLite[], place: SheetPlace, home: Point | null,
  region: Region, towns: readonly RegionTown[],
) {
  // `When` is dropped: the head states it, in the row's own words, and a sheet
  // that says the date twice in two formats is a sheet with two answers.
  // `Tickets` there is the price. WP 10.2 gave the sheet a "Tickets:" line
  // that means availability, and one word naming two facts on one sheet read
  // as "Tickets: 2 of 3 dates available" over "Tickets  Not given" — so here,
  // and only here, the price is called what it is.
  const out = sheetFacts(e, home, region, towns)
    .filter(f => f.label !== 'When')
    .map(f => (f.label === 'Tickets' ? { ...f, label: 'Price' } : f))
    // UI-audit a1-01: a night at another venue than the row's is that
    // venue's address, never its name on the row's street — and where the
    // lookup has neither, no Where at all (a1-31: only facts that are known).
    .map(f => (f.label === 'Where' && !place.own ? { ...f, value: placeAddress(place) } : f))
    .filter(f => f.label !== 'Where' || !!f.value)
  if (isFilm) {
    const p = e.performers ?? {}
    if (p.director) out.unshift({ label: 'Director', value: p.director })
    if (e.film?.year) out.unshift({ label: 'Year', value: String(e.film.year) })
    const scores = filmScores(e)
    // `sheetFacts` already prints Ratings for a screening; a release is the
    // one film kind it does not reach with a runtime, so the guard is on the
    // duplicate rather than on the kind.
    if (scores.length && !out.some(f => f.label === 'Ratings')) {
      out.push({ label: 'Ratings', value: scores.map(s => `${s.label} ${s.value}`).join(' · ') })
    }
  }
  // One length (UI-audit a1-11): a film's Runtime is TMDb's, and a second
  // row saying the same minutes as Duration is the sheet listing it twice.
  if (e.duration_min != null && !out.some(f => f.label === 'Duration' || f.label === 'Runtime')) {
    out.push({ label: 'Duration', value: formatDuration(e.duration_min) })
  }
  // a1-31: the language only where it is known — "Unclear" is not a fact.
  const lang = knownLanguage(e)
  if (lang && !out.some(f => f.label === 'Language')) out.push({ label: 'Language', value: lang })
  // films-sunday WP B: a film's language is a fact of each showing — the
  // same film plays OmU on Wednesday and dubbed on Saturday — so the row
  // names the versions the dates carry, and says exactly what it said before
  // where none carries one (`languageLine`). Over the chips' own nights
  // (UI-audit a1-11) — future, one copy each — not every showing the row
  // holds: a past night, or a second source's copy of a night with no
  // version printed, made "OmeU on 6 dates" beside eleven cinemas whose
  // chips all said OmeU.
  //
  // a1-31: a film whose language nobody knows still has a Language row when
  // its nights print a version — the versions are then the whole line.
  if (isFilm) {
    const line = languageLine(lang ?? '', occurrences, zoneOfCity(e.city))
    const at = out.findIndex(f => f.label === 'Language')
    if (at >= 0) out[at] = { ...out[at], value: line }
    else if (line) out.push({ label: 'Language', value: line })
  }
  if (e.url && hostOf(e.url)) out.push({ label: 'Source', value: hostOf(e.url)!, href: e.url })
  return out
}

/**
 * Nightlife N7: a club night's printed lineup as a text row, in front of
 * `Where` — only where no DJ or Live chip will be drawn. The chips come from
 * `entry_people` (N2's `peopleFor`), which a night harvested before N2 ran,
 * or one whose `feed_detail` is still in flight, does not have yet; the
 * lineup is on the row itself. Both at once would be the sheet naming every
 * DJ twice. All names, not the row's four: the sheet has the room.
 */
function withLineup(rows: Fact[], e: FeedRow, night: FeedRow['night'], groups: Group[]): Fact[] {
  if (e.type !== 'club_night') return rows
  if (groups.some(g => g.chips.some(c => c.role === 'dj' || c.role === 'live'))) return rows
  const line = lineupLine(night, Number.MAX_SAFE_INTEGER)
  if (!line) return rows
  const where = rows.findIndex(f => f.label === 'Where')
  const at = where < 0 ? rows.length : where
  return [...rows.slice(0, at), { label: 'Lineup', value: line }, ...rows.slice(at)]
}

/**
 * The fact list with the chips folded into it (E5, rule 1).
 *
 * The rule is *the sheet keeps its shape*, so nothing moves: a row that
 * already names a role — `Orchestra`, `Conductor`, `Artists` — keeps its
 * label and its position in the `<dl>` and gets chips in its `<dd>` instead
 * of the string. A role with no row of its own (a concert's composers, which
 * have only ever been in the programme block; an opera's ensemble, where the
 * house prints no orchestra) is a new row placed immediately after the last
 * one that did match, so the people stay together and the rows below them
 * stay where they were. With no people rows at all it goes in front of
 * `Where`, which is where the per-kind facts end and the four every kind
 * shares begin.
 *
 * `groups` is empty while `feed_detail` is in flight and for a row with no
 * edges at all (a film: TMDb holds its director on `works`), and then this
 * returns the facts untouched — which is rule 2, the `<dd>` showing the
 * fact's own string rather than flashing empty.
 */
function withChips(rows: Fact[], groups: Group[]): Fact[] {
  if (groups.length === 0) return rows

  // Which existing row each group takes over. A row that matches keeps its
  // own index — it is not moved into a block with the others, which is what
  // "stay where they are" means for the row you already knew was there.
  const taken = new Map<number, Group>()
  const added: Fact[] = []
  for (const g of groups) {
    const role = g.chips[0]?.role ?? ''
    const want = PEOPLE_FACT_LABEL[role]
    const at = want ? rows.findIndex((f, i) => !taken.has(i) && f.label === want) : -1
    if (at >= 0) taken.set(at, g)
    else added.push({ label: g.label, value: '', chips: g.chips })
  }

  const last = taken.size > 0 ? Math.max(...taken.keys()) : -1
  const where = rows.findIndex(f => f.label === 'Where')
  // Where a group with no row of its own goes: after the last row that did
  // match, else in front of `Where` — `- 1` because a row is emitted *after*
  // the index it follows.
  const anchor = last >= 0 ? last : where - 1

  const out: Fact[] = []
  rows.forEach((f, i) => {
    const g = taken.get(i)
    out.push(g ? { label: f.label, value: f.value, chips: g.chips } : f)
    if (i === anchor) out.push(...added)
  })
  if (anchor < 0) out.unshift(...added)
  else if (anchor >= rows.length) out.push(...added)
  return out
}

/** The programme in full, main work marked. The row only ever has room for the
 *  anchor. */
function programme(e: FeedRow): { text: string; main: boolean }[] {
  if (e.type !== 'concert') return []
  return (e.classical?.programme ?? []).map(item => {
    const composer = item.composer ?? item.composer_src
    const work = item.work ?? item.work_src
    return { text: composer ? `${composer} — ${work}` : String(work), main: !!item.is_main }
  })
}

/** A link and a headline, never a summary — matched nightly by house and title
 *  (`src/reviews.ts`), and for a film by `work_id`. */
function reviews(e: FeedRow) {
  return [...(e.stage?.reviews ?? []), ...(e.film?.reviews ?? [])]
}
